import { useState } from "react";
import {
  Building2,
  User,
  Phone,
  Mail,
  Edit2,
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
  Gift,
  Percent,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ErrorState } from "@/components/PageLayout";
import { AccountLayout } from "@/components/account/AccountLayout";
import { OrderImportActions } from "@/components/order-import/OrderImportActions";
import { Skeleton } from "@/components/ui/skeleton";
import { useDashboardData } from "@/hooks/use-dashboard";
import { useUpdateCompany, useUpdateContacts } from "@/hooks/use-update-account";
import { getDashboardStatIcon } from "@/lib/icon-map";
import { toast } from "sonner";
import type { CompanyData } from "@/types/api";

type CompanyFormState = Pick<
  CompanyData,
  "name" | "inn" | "kpp" | "ogrn" | "legalAddress" | "actualAddress"
>;
type ContactsFormState = Pick<CompanyData, "contactPerson" | "phone" | "email">;

export default function AccountDashboard() {
  const [offerSlide, setOfferSlide] = useState(0);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState<CompanyFormState>({
    name: "",
    inn: "",
    kpp: "",
    ogrn: "",
    legalAddress: "",
    actualAddress: "",
  });
  const [contactsForm, setContactsForm] = useState<ContactsFormState>({
    contactPerson: "",
    phone: "",
    email: "",
  });

  const { data, isLoading, isError, refetch } = useDashboardData();
  const updateCompany = useUpdateCompany();
  const updateContacts = useUpdateContacts();

  function openCompanyDialog(cd: CompanyData) {
    setCompanyForm({
      name: cd.name,
      inn: cd.inn,
      kpp: cd.kpp,
      ogrn: cd.ogrn,
      legalAddress: cd.legalAddress,
      actualAddress: cd.actualAddress,
    });
    setCompanyDialogOpen(true);
  }

  function openContactsDialog(cd: CompanyData) {
    setContactsForm({ contactPerson: cd.contactPerson, phone: cd.phone, email: cd.email });
    setContactsDialogOpen(true);
  }

  function handleCompanySubmit(e: React.FormEvent) {
    e.preventDefault();
    updateCompany.mutate(companyForm, {
      onSuccess: () => {
        setCompanyDialogOpen(false);
        toast.success("Данные компании обновлены");
      },
      onError: () => toast.error("Не удалось сохранить данные"),
    });
  }

  function handleContactsSubmit(e: React.FormEvent) {
    e.preventDefault();
    updateContacts.mutate(contactsForm, {
      onSuccess: () => {
        setContactsDialogOpen(false);
        toast.success("Контактные данные обновлены");
      },
      onError: () => toast.error("Не удалось сохранить данные"),
    });
  }

  if (isLoading) {
    return (
      <AccountLayout title="Личный кабинет" actions={<OrderImportActions />}>
        <div className="space-y-6">
          <Skeleton className="h-40 w-full rounded-lg" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-64 w-full rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        </div>
      </AccountLayout>
    );
  }

  if (isError || !data) {
    return (
      <AccountLayout title="Личный кабинет" actions={<OrderImportActions />}>
        <ErrorState title="Не удалось загрузить данные кабинета" onRetry={() => refetch()} />
      </AccountLayout>
    );
  }

  const { companyData, specialOffers, dashboardStats } = data;

  return (
    <AccountLayout title="Личный кабинет" actions={<OrderImportActions />}>
      {/* Special offers banner */}
      <div className="relative mb-6">
        <div className="section-block relative h-44 overflow-hidden md:h-40">
          {specialOffers.map((offer, index) => (
            <div
              key={offer.id}
              className={`absolute inset-0 transition-all duration-500 ease-out ${
                index === offerSlide
                  ? "translate-x-0 opacity-100"
                  : index < offerSlide
                    ? "-translate-x-8 opacity-0"
                    : "translate-x-8 opacity-0"
              }`}
            >
              <img
                src={offer.image}
                alt={offer.title}
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
              {/* pb-12 on phones keeps the two-line description clear of the dot row. */}
              <div className="absolute inset-0 flex items-center p-4 pb-12 md:p-6">
                <div className="text-white max-w-md">
                  <div className="flex items-center gap-2 mb-2">
                    {offer.type === "discount" && <Percent className="size-5 text-primary" />}
                    {offer.type === "delivery" && <ShoppingBag className="size-5 text-primary" />}
                    {offer.type === "gift" && <Gift className="size-5 text-primary" />}
                    <span className="text-xs uppercase tracking-wider text-primary font-medium">
                      Специальное предложение
                    </span>
                  </div>
                  <h3 className="section-heading mb-1 text-white">{offer.title}</h3>
                  <p className="text-white/80 text-sm line-clamp-2">{offer.description}</p>
                </div>
              </div>
            </div>
          ))}

          {/* Dots */}
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-20 flex">
            {specialOffers.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Предложение ${index + 1} из ${specialOffers.length}`}
                aria-current={index === offerSlide}
                onClick={() => setOfferSlide(index)}
                // The visible dot is 8px; the button around it is the tap target.
                className="flex size-10 items-center justify-center"
              >
                <span
                  className={`size-2 rounded-full transition-all ${
                    index === offerSlide ? "scale-110 bg-primary" : "bg-card/60"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Slider Controls - hidden on mobile */}
        <button
          type="button"
          aria-label="Предыдущее предложение"
          onClick={() => setOfferSlide(Math.max(0, offerSlide - 1))}
          disabled={offerSlide === 0}
          className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-20 size-10 bg-card border border-border rounded-full items-center justify-center text-foreground hover:bg-secondary transition-colors shadow-lifted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="size-5" />
        </button>
        <button
          type="button"
          aria-label="Следующее предложение"
          onClick={() => setOfferSlide(Math.min(specialOffers.length - 1, offerSlide + 1))}
          disabled={offerSlide >= specialOffers.length - 1}
          className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 size-10 bg-card border border-border rounded-full items-center justify-center text-foreground hover:bg-secondary transition-colors shadow-lifted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
        {dashboardStats.map((stat) => {
          const StatIcon = getDashboardStatIcon(stat.icon);
          return (
            <div key={stat.label} className="section-block p-4">
              <div className="flex items-center gap-3">
                <div
                  className={`size-10 rounded-lg bg-secondary flex items-center justify-center ${stat.color}`}
                >
                  <StatIcon className="size-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-4 md:gap-6 [grid-template-columns:repeat(auto-fit,minmax(min(100%,430px),1fr))]">
        {/* Company info */}
        <div className="min-w-0 section-block overflow-hidden">
          <div className="px-4 py-3 md:px-6 border-b border-border flex items-center justify-between">
            <h2 className="section-heading flex items-center gap-2">
              <Building2 className="size-5 text-primary" />
              Данные компании
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="text-sm"
              aria-label="Редактировать данные компании"
              onClick={() => openCompanyDialog(companyData)}
            >
              <Edit2 className="size-4 mr-1" />
              <span className="hidden sm:inline">Редактировать</span>
            </Button>
          </div>
          <div className="p-4 md:p-6 space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Название</p>
              <p className="text-base font-medium text-foreground">{companyData.name}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
              <div>
                <p className="text-sm text-muted-foreground">ИНН</p>
                <p className="text-base font-medium text-foreground break-words tabular-nums">
                  {companyData.inn}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">КПП</p>
                <p className="text-base font-medium text-foreground break-words tabular-nums">
                  {companyData.kpp}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">ОГРН</p>
                <p className="text-base font-medium text-foreground whitespace-nowrap tabular-nums">
                  {companyData.ogrn}
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Юридический адрес</p>
              <p className="text-base font-medium text-foreground">{companyData.legalAddress}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Фактический адрес</p>
              <p className="text-base font-medium text-foreground">{companyData.actualAddress}</p>
            </div>
          </div>
        </div>

        {/* Contact info */}
        <div className="min-w-0 section-block overflow-hidden">
          <div className="px-4 py-3 md:px-6 border-b border-border flex items-center justify-between">
            <h2 className="section-heading flex items-center gap-2">
              <User className="size-5 text-primary" />
              Контактные данные
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="text-sm"
              aria-label="Редактировать контактные данные"
              onClick={() => openContactsDialog(companyData)}
            >
              <Edit2 className="size-4 mr-1" />
              <span className="hidden sm:inline">Редактировать</span>
            </Button>
          </div>
          <div className="p-4 md:p-6">
            {/* Profile photo */}
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
              {/* No avatar-upload endpoint exists, so the camera affordance that used
                  to sit here was a button with no onClick. Dropped until there is one. */}
              <div className="size-12 md:size-16 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="size-6 md:size-10 text-primary" />
              </div>
              <div>
                <p className="text-base font-medium text-foreground">{companyData.contactPerson}</p>
                <p className="text-sm text-muted-foreground">Контактное лицо</p>
              </div>
            </div>

            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center gap-3">
                <Phone className="size-4 md:size-5 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-sm text-muted-foreground">Телефон</p>
                  <p className="text-base font-medium text-foreground">{companyData.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="size-4 md:size-5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="text-base font-medium text-foreground break-words">
                    {companyData.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Manager */}
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground mb-2">Ваш персональный менеджер</p>
              <div className="flex items-center justify-between bg-secondary rounded-lg p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="size-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {companyData.manager}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {companyData.managerPhone}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit company dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Данные компании</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCompanySubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="c-name">Название</Label>
              <Input
                id="c-name"
                autoComplete="organization"
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="c-inn">ИНН</Label>
                <Input
                  id="c-inn"
                  inputMode="numeric"
                  value={companyForm.inn}
                  onChange={(e) => setCompanyForm({ ...companyForm, inn: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-kpp">КПП</Label>
                <Input
                  id="c-kpp"
                  inputMode="numeric"
                  value={companyForm.kpp}
                  onChange={(e) => setCompanyForm({ ...companyForm, kpp: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-ogrn">ОГРН</Label>
                <Input
                  id="c-ogrn"
                  inputMode="numeric"
                  value={companyForm.ogrn}
                  onChange={(e) => setCompanyForm({ ...companyForm, ogrn: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-legal">Юридический адрес</Label>
              <Input
                id="c-legal"
                value={companyForm.legalAddress}
                onChange={(e) => setCompanyForm({ ...companyForm, legalAddress: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-actual">Фактический адрес</Label>
              <Input
                id="c-actual"
                value={companyForm.actualAddress}
                onChange={(e) => setCompanyForm({ ...companyForm, actualAddress: e.target.value })}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCompanyDialogOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={updateCompany.isPending}>
                {updateCompany.isPending ? "Сохранение..." : "Сохранить"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit contacts dialog */}
      <Dialog open={contactsDialogOpen} onOpenChange={setContactsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Контактные данные</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleContactsSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="ct-person">Контактное лицо</Label>
              <Input
                id="ct-person"
                autoComplete="name"
                value={contactsForm.contactPerson}
                onChange={(e) =>
                  setContactsForm({ ...contactsForm, contactPerson: e.target.value })
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ct-phone">Телефон</Label>
              <Input
                id="ct-phone"
                autoComplete="tel"
                type="tel"
                value={contactsForm.phone}
                onChange={(e) => setContactsForm({ ...contactsForm, phone: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ct-email">Email</Label>
              <Input
                id="ct-email"
                autoComplete="email"
                type="email"
                value={contactsForm.email}
                onChange={(e) => setContactsForm({ ...contactsForm, email: e.target.value })}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContactsDialogOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={updateContacts.isPending}>
                {updateContacts.isPending ? "Сохранение..." : "Сохранить"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AccountLayout>
  );
}
